import { Injectable, inject } from '@angular/core';
import {
  CollectionReference,
  DocumentReference,
  DocumentSnapshot,
  Firestore,
  Query,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getAggregateFromServer,
  getCountFromServer,
  getDoc,
  getDocs,
  getDocsFromCache,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where
} from '@angular/fire/firestore';

export interface Params {
  where?: {
    key: string,
    condition: "<" | "<=" | "==" | ">" | ">=" | "!=" | "array-contains" | "array-contains-any" | "in" | "not-in",
    value: any
  }[],
  orders?: {
    fieldPath: string,
    direction: "asc" | "desc"
  }[],
  limit?: number,
  collectionGroupName?: string
}

@Injectable({
  providedIn: 'root'
})
export class FirestoreService {

  private cacheTime: number = 30;

  protected firestore: Firestore = inject(Firestore);
  protected collectionRef!: CollectionReference;

  protected unsubscribes: any = {};

  constructor() { }

  setCacheTime(time: number) {
    this.cacheTime = time
  }

  getCacheDate() {
    const collectionName = this.collectionRef.path
    const _cacheDate = localStorage.getItem(collectionName + '_cacheDate')

    if (_cacheDate) {
      return new Date(_cacheDate)
    }

    const cacheDate = new Date()
    this.setCacheDate(cacheDate)
    return cacheDate
  }

  setCacheDate(date: Date) {
    const collectionName = this.collectionRef.path
    date.setSeconds(date.getSeconds() + this.cacheTime)

    localStorage.setItem(collectionName + '_cacheDate', date.toISOString())
  }

  resetCacheDate() {
    const collectionName = this.collectionRef.path

    const date = new Date()
    date.setSeconds(date.getSeconds() - 1000)

    localStorage.setItem(collectionName + '_cacheDate', date.toISOString())
  }

  public setCollectionRef(collectionName: string) {
    this.collectionRef = collection(this.firestore, collectionName);
  }

  public setCollectionRefByRef(collectionRef: CollectionReference) {
    this.collectionRef = collectionRef;
  }

  public setSubCollectionRefByRef(documentRef: DocumentReference, collectionName: string) {
    this.collectionRef = collection(documentRef, collectionName);
  }

  public setSubCollection(collectionName: string, subCollectionName: string, id: string) {
    this.setCollectionRef(collectionName);
    this.setSubCollectionRefByRef(this.getDocRef(id), subCollectionName);
  }

  public setSubCollectionFromSubCollection(
    collectionName: string,
    parentId: string,
    subCollectionName: string,
    subDocId: string,
    finalSubCollectionName: string
  ) {
    this.setCollectionRef(collectionName);
    const parentDocRef = this.getDocRef(parentId);
    const subCollectionRef = collection(parentDocRef, subCollectionName);
    const subDocRef = doc(subCollectionRef, subDocId);
    this.collectionRef = collection(subDocRef, finalSubCollectionName);
  }

  public setSubCollectionFromSubCollectionFromSubCollection(
    collectionName: string,
    parentId: string,
    subCollection1: string,
    subDoc1: string,
    subCollection2: string,
    subDoc2: string,
    finalSubCollection: string
  ) {
    this.setCollectionRef(collectionName);

    const parentDocRef = this.getDocRef(parentId);
    const sub1Ref = doc(collection(parentDocRef, subCollection1), subDoc1);
    const sub2Ref = doc(collection(sub1Ref, subCollection2), subDoc2);

    this.collectionRef = collection(sub2Ref, finalSubCollection);
  }

  public getCollectionRef() {
    return this.collectionRef;
  }

  public async loadDocs(params: Params = {}, skipCache = false) {
    let q = this.getQuery(params)
    const cacheDate = this.getCacheDate();

    if (!skipCache && cacheDate > new Date()) {
      const snap = await getDocsFromCache(q);
      if (snap?.size === 0) {
        this.resetCacheDate();
        return getDocs(q);
      }

      return snap
    }

    this.setCacheDate(new Date());
    return getDocs(q);
  }

  public async load(params: Params = {}, skipCache = false) {
    let q = this.getQuery(params);

    let data: any[] = [];
    const querySnapshot = await this.loadDocs(params, skipCache);
    querySnapshot.forEach((docSnap) => {
      const docData = docSnap.data();
      if (!docData['id']) docData['id'] = docSnap['id'];
      data.push(docData);
    });

    return data;
  }

  public async loadPaginated(params: Params = {}, skipCache = false) {
    const querySnapshot = await this.loadDocs(params, skipCache);

    const data: any[] = [];

    querySnapshot.forEach((docSnap) => {
      const docData = docSnap.data();

      if (!docData['id']) {
        docData['id'] = docSnap.id;
      }

      data.push(docData);
    });

    return {
      items: data as any[],
      lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1] ?? null,
      hasMore: querySnapshot.docs.length === params.limit
    };
  }

  protected async getAggregateFromServer(params: Params = {}, consult: any) {
    const q = this.getQuery(params);

    const querySnapshot = await getAggregateFromServer(q, consult);

    if (querySnapshot.data()) {
      return querySnapshot.data();
    } else {
      return false;
    }
  }

  getQuery(params: Params = {}) {
    let q: Query = params?.collectionGroupName ? collectionGroup(this.firestore, params.collectionGroupName) : query(this.collectionRef);

    if (params.where) {
      params.where.forEach((doc: any) => {
        q = query(q, where(doc.key, doc.condition, doc.value));
      });
    }

    if (params.orders) {
      params.orders.forEach((doc: any) => {
        q = query(q, orderBy(doc.fieldPath, doc.direction));
      });
    }

    if (params.limit) {
      q = query(q, limit(params.limit));
    }

    return q;
  }

  onSnapshot(path: any, callback: Function) {
    return onSnapshot(path, (doc: DocumentSnapshot) => {
      callback(doc);
    });
  }

  doOnSnapshot(path: DocumentReference): Promise<any> {
    return new Promise((resolve, reject) => {
      const unsubscribe = onSnapshot(path, { includeMetadataChanges: true }, snapshot => {
        const data = snapshot.data();
        if (data && snapshot.id) {
          data['id'] = snapshot.id;
        }

        unsubscribe();
        resolve(data);
      }, err => {
        unsubscribe();
        reject(err);
      });
    });
  }

  async add(item: any): Promise<any> {
    item.id = this.generateId();

    return this.set(item);
  }

  async set(item: any, id?: string): Promise<any> {
    this.resetCacheDate();
    if (!id) id = item.id;

    const path = this.getDocRef(id!);
    setDoc(path, this.verifyUndefinedFields(item), { merge: true });
    return this.doOnSnapshot(path);
  }

  setDoc(item: any, id?: string): Promise<any> {
    this.resetCacheDate();
    if (!id) id = item.id;

    const path = this.getDocRef(id!);
    return setDoc(path, this.verifyUndefinedFields(item), { merge: true });
  }

  public async addOrUpdate(document: any): Promise<any> {
    const now = new Date();
    document.updatedAt = now;
    if (!document.id) {
      const idDocument = this.generateId();
      document.id = idDocument;
      document.createdAt = now;
    }

    return this.set(document);
  }

  async update(item: any): Promise<any> {
    const now = new Date();
    item.updatedAt = now;
    const path = this.getDocRef(item.id);

    updateDoc(path, item);
    return this.doOnSnapshot(path);
  }

  async deleteDoc(itemId: string): Promise<void> {
    this.resetCacheDate();
    const path = this.getDocRef(itemId);
    deleteDoc(path);
    await this.doOnSnapshot(path);
    return;
  }

  public async get(docId: string): Promise<any> {
    const docSnap = await this.getDocSnap(docId);

    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      return false;
    }
  }

  public getDocRef(docId: string) {
    return doc(this.collectionRef, docId);
  }

  public async getDocByRef(docRef: DocumentReference): Promise<any> {
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      return false;
    }
  }

  public getDocSnap(docId: string): Promise<DocumentSnapshot> {
    const docRef = this.getDocRef(docId);
    return getDoc(docRef);
  }

  public generateId() {
    return doc(this.collectionRef).id;
  }

  public async count(params: Params = {}) {
    let q = this.getQuery(params);
    const snapshot = await getCountFromServer(q);

    return snapshot.data()?.count;
  }

  unsubscribe(funcName: string) {
    if (this.unsubscribes[funcName]) {
      this.unsubscribes[funcName]();
    }
  }

  subscribe(funcName: string, path: any, callback: Function) {
    this.unsubscribes[funcName] = this.onSnapshot(path, (doc: DocumentSnapshot) => {
      callback(doc);
    });
  }

  verifyUndefinedFields(item: any) {
    if (!item) return item;

    if (typeof item === 'object') {
      const result = { ...item };

      Object.keys(result).forEach((key) => {
        if (result[key] === undefined) {
          result[key] = null;
        }
      });

      return result;
    }

    return item;
  }
}
